import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { setAccessToken, setRefreshToken } from "auth/tokenStorage";
import api from "api/axios";
import { useAuthContext } from "auth/AuthProvider";
import { handleAxiosAndCaughtError } from "utils";
import { PERMISSIONS } from "auth/permissions";

// Helper function to check if user has FULL_CONTROL permission directly on the user data
const checkHasFullControl = (userData: any): boolean => {
  if (!userData || !userData.current_workspace?.permissions) {
    return false;
  }
  const permissions = userData.current_workspace.permissions;
  return permissions.includes(PERMISSIONS.FULL_CONTROL);
};

export function useSignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setIsAuthenticated, fetchUser } = useAuthContext();

  const handleSignIn = async () => {
    setError("");
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await api.post("/auth/login", {
        email,
        password,
      });
      const { access_token, refresh_token, expires_in, refresh_expires_in } =
        res.data;
      setAccessToken(access_token, expires_in);
      setRefreshToken(refresh_token, refresh_expires_in);
      
      setIsAuthenticated(true);
      
      // Fetch user data and get the returned user object
      const userData = await fetchUser();
      
      // Redirect based on FULL_CONTROL permission
      if (userData) {
        const isFullControl = checkHasFullControl(userData);
        navigate(isFullControl ? "/dashboard" : "/chat");
      } else {
        navigate("/chat");
      }
    } catch (err: any) {
      setError("Invalid email or password.");
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      handleSignIn();
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    error,
    isLoading,
    handleSignIn,
    handleKeyPress,
  };
}
